import { Tooltip, Typography } from "@mui/material";
import makeStyles from '@mui/styles/makeStyles';
import React, { useMemo } from "react";
import { sanitizeEmailContent } from "../../hooks/useEmailPreview";

const useStyles = makeStyles(() => ({
    body: {
        width: '100%',
        textAlign: 'left',
        whiteSpace: 'pre-wrap',
        wordBreak: 'break-word',
        fontSize: 'inherit',
        lineHeight: 'inherit',
        color: 'inherit',
        fontFamily: 'inherit',
    },
    headerLine: {
        fontSize: '0.85em',
        color: '#94a3b8',
        fontWeight: 400,
    },
    forwardedSeparator: {
        fontSize: '0.85em',
        color: '#94a3b8',
        fontWeight: 400,
    },
    link: {
        color: '#0d9488',
        textDecoration: 'underline',
        textDecorationColor: '#99f6e4',
        textUnderlineOffset: '2px',
        fontWeight: 500,
        transition: 'all 0.15s',
        cursor: 'pointer',
        '&:hover': {
            color: '#0f766e',
            textDecorationColor: '#0d9488',
        },
    },
    tooltip: {
        backgroundColor: '#fff',
        color: '#374151',
        fontSize: '12px',
        fontFamily: 'Inter, sans-serif',
        border: '1px solid #e5e7eb',
        borderRadius: '6px',
        padding: '6px 10px',
        boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
        maxWidth: 400,
        wordBreak: 'break-all',
    },
    tooltipArrow: {
        color: '#fff',
        '&::before': {
            border: '1px solid #e5e7eb',
        },
    },
    bold: {
        fontWeight: 600,
    },
    quotedLine: {
        color: '#9ca3af',
        paddingLeft: '10px',
        borderLeft: '2px solid #e5e7eb',
        display: 'block',
        fontSize: '0.9em',
        lineHeight: 1.4,
    },
}));

type Segment =
    | { type: 'text'; text: string }
    | { type: 'link'; url: string; display: string }
    | { type: 'bold'; text: string };

const HEADER_RE = /^(From|To|Cc|Bcc|Subject|Date|Sent|Reply-To):\s*.+$/i;
const FORWARDED_RE = /^-{5,}\s*Forwarded message\s*-{5,}$/;

function ensureProtocol(url: string): string {
    if (/^https?:\/\//.test(url)) return url;
    return 'https://' + url;
}

function shortenUrl(url: string): string {
    try {
        const parsed = new URL(url);
        let display = parsed.hostname.replace(/^www\./, '') + (parsed.pathname !== '/' ? parsed.pathname : '');
        if (display.length > 50) display = display.slice(0, 47) + '...';
        return display;
    } catch {
        return url;
    }
}

/** Parse inline segments (links, bold) within a single line of text. */
function parseLineSegments(text: string): Segment[] {
    const TOKEN =
        /([A-Za-z][^<*\n]*?) *<((?:https?:\/\/|www\.)[^\s<>]+)>|<((?:https?:\/\/|www\.)[^\s<>]+)>|(https?:\/\/[^\s<>"')\]]+)|\*([^*\n]+)\*/g;

    const segments: Segment[] = [];
    let lastIndex = 0;
    let match: RegExpExecArray | null;

    while ((match = TOKEN.exec(text)) !== null) {
        if (match.index > lastIndex) {
            segments.push({ type: 'text', text: text.slice(lastIndex, match.index) });
        }

        if (match[1] != null && match[2] != null) {
            const label = match[1].trim();
            const url = ensureProtocol(match[2]);
            segments.push({ type: 'link', url, display: label });
        } else if (match[3] != null) {
            const url = ensureProtocol(match[3]);
            segments.push({ type: 'link', url, display: shortenUrl(url) });
        } else if (match[4] != null) {
            const rawUrl = match[4];
            const cleanUrl = rawUrl.replace(/[.,;:!?)]+$/, '');
            const trailing = rawUrl.slice(cleanUrl.length);
            segments.push({ type: 'link', url: cleanUrl, display: shortenUrl(cleanUrl) });
            if (trailing) segments.push({ type: 'text', text: trailing });
        } else if (match[5] != null) {
            segments.push({ type: 'bold', text: match[5] });
        }

        lastIndex = match.index + match[0].length;
    }

    if (lastIndex < text.length) {
        segments.push({ type: 'text', text: text.slice(lastIndex) });
    }

    return segments;
}

interface FormattedEmailBodyProps {
    content: string | null | undefined;
    className?: string;
}

const FormattedEmailBody: React.FC<FormattedEmailBodyProps> = ({ content, className }) => {
    const classes = useStyles();

    const lines = useMemo(() => {
        const text = sanitizeEmailContent(content) || 'No content.';
        const parsed = text.split('\n').map(line => {
            const trimmed = line.trim();
            const quoteMatch = trimmed.match(/^(>+)\s?(.*)/);
            const isQuoted = !!quoteMatch;
            const displayText = isQuoted ? (quoteMatch[2] ?? '') : line;
            const isHeader = !isQuoted && HEADER_RE.test(trimmed);
            const isForwarded = !isQuoted && FORWARDED_RE.test(trimmed);
            return { text: displayText, isHeader, isForwarded, isQuoted };
        });

        // Mark continuation lines of wrapped headers (e.g. long Subject:)
        for (let i = 1; i < parsed.length; i++) {
            const prev = parsed[i - 1];
            const curr = parsed[i];
            if (!prev.isHeader || curr.isHeader || curr.isForwarded || curr.isQuoted) continue;
            const trimmed = curr.text.trim();
            if (!trimmed) continue;
            // Not a new header field → continuation of the previous one
            if (!HEADER_RE.test(trimmed)) {
                curr.isHeader = true;
            }
        }

        return parsed;
    }, [content]);

    return (
        <Typography component="div" className={`${classes.body} ${className || ''}`}>
            {lines.map((line, i) => {
                // Skip empty quoted lines (bare ">")
                if (line.isQuoted && !line.text.trim()) return null;

                const lineClass = line.isQuoted
                    ? classes.quotedLine
                    : line.isHeader
                        ? classes.headerLine
                        : line.isForwarded
                            ? classes.forwardedSeparator
                            : undefined;

                const segments = parseLineSegments(line.text);
                const rendered = segments.map((seg, j) => {
                    if (seg.type === 'link') {
                        return (
                            <Tooltip key={j} title={seg.url} arrow placement="top"
                                classes={{ tooltip: classes.tooltip, arrow: classes.tooltipArrow }}>
                                <a href={seg.url}
                                    className={classes.link}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    onClick={(e) => e.stopPropagation()}>
                                    {seg.display}
                                </a>
                            </Tooltip>
                        );
                    }
                    if (seg.type === 'bold') {
                        return <span key={j} className={classes.bold}>{seg.text}</span>;
                    }
                    return <React.Fragment key={j}>{seg.text}</React.Fragment>;
                });

                return (
                    <React.Fragment key={i}>
                        {lineClass
                            ? <span className={lineClass}>{rendered}</span>
                            : rendered}
                        {i < lines.length - 1 ? '\n' : null}
                    </React.Fragment>
                );
            })}
        </Typography>
    );
};

export default FormattedEmailBody;
